import {
  ElementNode,
  Text,
  View,
  Show,
  hexColor,
  For,
} from "@lightningtv/solid";
import { Row, Button, Column } from "@lightningtv/solid-ui";
import { useParams, useNavigate, useLocation } from "@solidjs/router";
import { createEffect, createResource, createSignal, onMount } from "solid-js";
import { playListPassword, playListUrl, playListUsername, setIsPlaying } from "../../state";
import ContentBlock from "../components/ContentBlock";
import * as provider from "../api/providers/entity";
import type { Tile } from "../api/formatters/ItemFormatter";
import videoPlayer from "../video";
import LoadingIndicator from "./loading/loading";
import { getCORSSafeImageUrl } from "../components/ImageCORSHandler";
import ConfirmPin from "../components/ConfirmPin";
import { Buffer } from "buffer";
import { dnsResolver } from "../api/providers/dnsResolver";
import { buildStreamUrl, fetchChannelToken } from "../utils/tokenHelper";
import { fixImageUrl } from "../utils/fixImageUrl";

const Entity = () => {
  const params = useParams();

  console.log('Params', params);

  let columnRef: any, backdropRef: any, entityActions: any;
  let settingsRef: any, privacyCheck: any;

  const navigate = useNavigate();
  const location = useLocation();

  const [movieName, setMovieName] = createSignal<string | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = createSignal(false);
  const [valueOfStream, setValueOfStream] = createSignal<string | null>(null);
  const [backgroundImageEntity, setBackGroundImage] = createSignal<string | null>(null);

  const stored = JSON.parse(localStorage.getItem("movieCategoryIDs") || "[]");
  const [lockedCategoryIDs, setLockedCategoryIDs] = createSignal(stored);

  const [isReady, setIsReady] = createSignal(false);
  // Jetstream: page lifts up when the bottom row gets focus
  const [suggestionsLifted, setSuggestionsLifted] = createSignal(false);
  // Remember the last pressed suggestion so the row comes back to it
  const [lastSuggestionIndex, setLastSuggestionIndex] = createSignal(0);
  const [backdropAlpha, setBackdropAlpha] = createSignal(0);
  const [containerExctencion, setContainerExctencion] = createSignal<string | null>(null);
  const [savedDataShow, setSavedData] = createSignal<boolean>(false);
  const [savedProgress, setSavedProgress] = createSignal<{ currentTime: number, duration?: number } | null>(null);

  // PIN-related state
  const [showPinEntry, setShowPinEntry] = createSignal(false);
  const [pinAction, setPinAction] = createSignal<"play" | "continue" | null>(null);
  const [pinErrorMessage, setPinErrorMessage] = createSignal("");
  const [movieGenre, setMovieGenre] = createSignal<string | null>(null);
  const [movieCategoryId, setMovieCategoryId] = createSignal<string | null>(null);
  const [currentMovieData, setCurrentMovieData] = createSignal<any>(null);

  // ✅ NEW: Track if we came from another entity page
  const [returnToOriginalPage, setReturnToOriginalPage] = createSignal(false);

  // Store refs to restore focus after PIN close
  let playButtonRef: any;
  let continueButtonRef: any;

  const [data]: any = createResource(() => ({ ...params }), provider.getInfo);
  const [recommendations] = createResource<any, Tile[]>(() => ({ ...params }), provider.getRecommendations);

  // ✅ NEW: Check on mount if we're returning from another entity
  onMount(() => {
    const isReturning = sessionStorage.getItem('returningFromEntity');

    if (isReturning === 'true') {
      console.log("📍 Detected navigation from suggestion");
      setReturnToOriginalPage(true);
      sessionStorage.removeItem('returningFromEntity');
    } else {
      console.log("📍 Fresh navigation to entity page");
      setReturnToOriginalPage(false);
    }
  });

  // Helper function to check if movie is adult content
  const isAdultContent = () => {
    const genre = movieGenre()?.toLowerCase() || "";
    const categoryId = movieCategoryId() || "";
    const lockedIds = lockedCategoryIDs();

    console.log("🔍 Checking adult content:", { genre, categoryId, lockedIds });

    const hasAdultGenre = ["adult", "xxx", "porn", "18+"].some(keyword =>
      genre.includes(keyword)
    );

    const isCategoryLocked = lockedIds.includes(categoryId);

    return hasAdultGenre || isCategoryLocked;
  };

  createEffect(async () => {
    const currentData: any = data();
    const newData: any = recommendations();

    console.log('currentData', currentData);
    console.log('recommendations', newData);

    if (videoPlayer) {
      videoPlayer.destroy();
    }

    if (currentData && newData) {
      setIsReady(true);
      setContainerExctencion(currentData.containerExtencion());

      if (currentData.backgroundImage()) {
        setBackGroundImage(currentData.backgroundImage());
      }

      setMovieName(currentData.heroContent.title());
      setValueOfStream(currentData.idOfStream());

      setCurrentMovieData(currentData);

      const genre = currentData.heroContent.genre?.() || "";
      setMovieGenre(genre);
      setMovieCategoryId(params.id || "");

      // ── DEBUG: full raw data dump — remove once URL issue is resolved ──
      console.log("🎬 Movie loaded:", {
        name: currentData.heroContent.title(),
        genre: genre,
        categoryId: params.id,
        isAdult: isAdultContent(),
        hasToken: currentData.token,
        hasTokenUrl: currentData.tokenUrl,
        hasDirectSource: currentData.direct_source
      });
      console.log("🔎 RAW currentData keys:", Object.keys(currentData));
      console.log("🔎 idOfStream():", currentData.idOfStream?.());
      console.log("🔎 containerExtencion():", currentData.containerExtencion?.());
      console.log("🔎 direct_source:", currentData.direct_source);
      console.log("🔎 token:", currentData.token);
      console.log("🔎 tokenUrl:", currentData.tokenUrl);
      // Log the full raw object (careful — may be large)
      try { console.log("🔎 RAW currentData JSON:", JSON.stringify(currentData, (k, v) => typeof v === "function" ? "[Function]" : v, 2).substring(0, 2000)); } catch (e) { }
    }
  });

  const columnY = 640;

  const Backdrop = {
    color: hexColor("#000000"),
    alpha: 0,
    width: 1900,
    height: 890,
    x: -160,
    y: columnY,
    borderRadius: 30,
  };

  // Helper function to build movie URL with token support
  const buildMovieUrl = async (streamId: string, extension: string) => {
    const movieData = currentMovieData();

    if (!movieData) {
      console.warn("⚠️ No movie data available, using traditional URL");
      return `${playListUrl()}/movie/${playListUsername()}/${playListPassword()}/${streamId}.${extension}`;
    }

    console.log("🔍 Building movie URL with token support");
    console.log("🔍 Movie token field:", movieData.token);
    console.log("🔍 Movie tokenUrl field:", movieData.tokenUrl);
    console.log("🔍 Movie direct_source field:", movieData.direct_source);

    let tokenData = null;
    try {
      tokenData = await fetchChannelToken(movieData);
      if (tokenData) {
        console.log("🎟️ ✅ Token data fetched for movie (complete URL)");
      }
    } catch (error) {
      console.error("❌ Token fetch error:", error);
    }

    let videoUrl: string;

    if (tokenData) {
      videoUrl = buildStreamUrl(movieData, "", tokenData);
      console.log("🔗 Using token data as complete URL");
      return videoUrl;
    } else {
      videoUrl = `${playListUrl()}/movie/${playListUsername()}/${playListPassword()}/${streamId}.${extension}`;
      console.log("🔗 Using traditional URL");
    }

    let finalVideoUrl = videoUrl;

    try {
      const dnsResult = await dnsResolver.transformUrl(videoUrl);
      if (typeof dnsResult === 'object' && dnsResult.url) {
        finalVideoUrl = dnsResult.url;
        console.log("🔗 DNS resolver applied");
      } else if (typeof dnsResult === 'string' && dnsResult !== videoUrl) {
        finalVideoUrl = dnsResult;
        console.log("🔗 DNS resolver applied (string)");
      }
    } catch (dnsError) {
      console.warn("⚠️ DNS resolver error:", dnsError);
      finalVideoUrl = videoUrl;
    }

    if (localStorage.getItem("vpn") === "on" && localStorage.getItem("proxy_url")) {
      const videoUrlProxy = Buffer.from(finalVideoUrl, "utf-8").toString("base64");
      finalVideoUrl = localStorage.getItem("proxy_url") + videoUrlProxy;
      console.log("🔗 VPN proxy applied");
    }

    console.log("🔗 Final movie URL:", finalVideoUrl.substring(0, 150) + "...");
    return finalVideoUrl;
  };

  async function onEnterTrailer() {
    if (valueOfStream()) {
      if (isAdultContent()) {
        console.log("🔒 Adult content detected, showing PIN");
        setPinAction("play");
        setShowPinEntry(true);
      } else {
        const streamId = valueOfStream();
        const extc = containerExctencion();
        const nameOfMovie = movieName();

        navigate(
          `/player?str=${streamId}&type=movie&name=${nameOfMovie}&extc=${extc}`
        );
      }
    }
  }

  async function onVodResume() {
    if (valueOfStream()) {
      const streamOfID = valueOfStream();
      const nameOfMovie = movieName();
      const extc = containerExctencion();
      const progress = savedProgress();

      if (isAdultContent()) {
        console.log("🔒 Adult content detected, showing PIN");
        setPinAction("continue");
        setShowPinEntry(true);
      } else if (progress && progress.currentTime > 0) {
        console.log(`▶️ Resuming movie from ${progress.currentTime}s`);

        navigate(
          `/player?str=${streamOfID}&type=movie&name=${nameOfMovie}&extc=${extc}&seek=${progress.currentTime}`
        );
      } else {
        console.warn("⚠️ No saved progress found, playing from start");
        await onEnterTrailer();
      }
    }
  }

  const handlePinConfirmed = () => {
    console.log("✅ PIN confirmed successfully");
    setShowPinEntry(false);
    setPinErrorMessage("");

    const action = pinAction();
    const streamOfID = valueOfStream();
    const nameOfMovie = movieName();
    const extc = containerExctencion();

    if (action === "play") {
      navigate(
        `/player?str=${streamOfID}&type=movie&name=${nameOfMovie}&extc=${extc}`
      );
    } else if (action === "continue") {
      const progress = savedProgress();
      if (progress && progress.currentTime > 0) {
        navigate(
          `/player?str=${streamOfID}&type=movie&name=${nameOfMovie}&extc=${extc}&seek=${progress.currentTime}`
        );
      } else {
        navigate(
          `/player?str=${streamOfID}&type=movie&name=${nameOfMovie}&extc=${extc}`
        );
      }
    }

    setPinAction(null);
  };

  const handlePinFailed = () => {
    console.log("❌ PIN verification failed");
    setPinErrorMessage("Incorrect PIN. Please try again.");

    setTimeout(() => {
      setPinErrorMessage("");
    }, 3000);
  };

  // Effect to check for saved progress
  createEffect(() => {
    const streamOfID = valueOfStream();
    const extc = containerExctencion();

    if (!streamOfID || !extc) {
      setSavedData(false);
      setSavedProgress(null);
      console.log("❌ No stream ID or extension, cannot check for saved progress");
      return;
    }

    const currentVideoUrl = `${playListUrl()}/movie/${playListUsername()}/${playListPassword()}/${streamOfID}.${extc}`;
    const savedData = localStorage.getItem("vod_resume");

    console.log("🔍 Checking for saved progress...");
    console.log("Current video URL:", currentVideoUrl);

    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        const { videoUrl, currentTime, duration } = parsed;

        console.log("📦 Found saved data:", { videoUrl, currentTime, duration });

        if (videoUrl === currentVideoUrl && currentTime > 0) {
          const isNearEnd = duration ? (currentTime / duration) > 0.95 : false;

          if (!isNearEnd) {
            setSavedData(true);
            setSavedProgress({ currentTime, duration });
            console.log(`✅ Continue watching available: ${formatTime(currentTime)}${duration ? ` / ${formatTime(duration)}` : ''}`);
          } else {
            setSavedData(false);
            setSavedProgress(null);
            console.log("⏭️ Movie was almost finished, hiding continue watching");
          }
        } else {
          setSavedData(false);
          setSavedProgress(null);
          console.log("❌ Video URL doesn't match or no progress");
        }
      } catch (error) {
        console.error("❌ Error parsing saved data:", error);
        setSavedData(false);
        setSavedProgress(null);
      }
    } else {
      setSavedData(false);
      setSavedProgress(null);
      console.log("ℹ️ No saved data found in localStorage");
    }
  });

  function formatTime(seconds: number): string {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  function SuggestionItem(props: any) {
    const movieData = props.data;
    const posterSrc = props.src || "/assets/movie-placeholder.png";
    const movieName = movieData?.name || props.shortTitle || "Unknown";
    const streamId = movieData?.stream_id;

    const handleEnter = () => {
      if (streamId) {
        // ✅ Set flag that we're navigating from a suggestion
        sessionStorage.setItem('returningFromEntity', 'true');
        console.log(`🚀 Navigating to suggested movie: ${streamId}`);

        // Scroll the page back up, remember which card was pressed and put
        // focus on the action buttons of the newly opened movie
        setLastSuggestionIndex(props.index ?? 0);
        setSuggestionsLifted(false);
        navigate(`/entity/movie/${streamId}/${params.id}`);
        setTimeout(() => {
          const target = playButtonRef || entityActions;
          if (target?.setFocus) target.setFocus();
        }, 100);
      }
    };

    return (
      <View
        style={{
          width: 258,
          height: 382,
          scale: 1,
          borderRadius: 10,
          border: { width: 0, color: hexColor("#ffffff00") },
          color: hexColor("#00000000"),
          transition: {
            scale: { duration: 250, easing: "ease-in-out" },
            border: { duration: 200 }
          },
          focus: {
            scale: 1.02,
            color: hexColor("#00000000"),
            border: { width: 6, color: hexColor("#ffffff") }
          },
          alignItems: "center",
          justifyContent: "center",
        }}
        onEnter={handleEnter}
      >
        <View
          // src={posterSrc}
          src={fixImageUrl(posterSrc)}
          style={{
            width: 248,
            height: 372,
            borderRadius: 10
          }}
          x={5}
          y={5}
        />
      </View>
    );
  }

  return (
    <Show when={data()}>
      {/* PIN Entry Overlay */}
      <Show when={showPinEntry()}>
        <View
          color={hexColor("#000000")}
          width={1920}
          height={1080}
          style={{ zIndex: 2000 }}
          onEscape={() => {
            console.log("🚫 Escape pressed on PIN overlay");
            setShowPinEntry(false);
            setPinAction(null);
            setPinErrorMessage("");
          }}
        >
          <View x={760} y={150}>
            <Text fontSize={48} color={"#ffffff"} textAlign={"center"} x={-20}>
              Adult Content - PIN Required
            </Text>
            <Text fontSize={32} color={"#cccccc"} textAlign={"center"} y={60} x={-60}>
              Please enter your PIN to continue
            </Text>

            <Show when={pinErrorMessage()}>
              <View x={-180} y={140} width={600} height={60} color={hexColor("#ff000080")} borderRadius={10}>
                <Text fontSize={24} color={"#ffffff"} textAlign={"center"} x={150} y={18}>
                  {pinErrorMessage()}
                </Text>
              </View>
            </Show>
          </View>
          <ConfirmPin
            onPinSet={handlePinConfirmed}
            onPinFailed={handlePinFailed}
            onEscape={() => {
              console.log("🚫 Escape from ConfirmPin");
              setShowPinEntry(false);
              const action = pinAction();
              setPinAction(null);
              setPinErrorMessage("");

              setTimeout(() => {
                if (action === "play" && playButtonRef) {
                  playButtonRef.setFocus();
                  console.log("✅ Focus restored to Play button");
                } else if (action === "continue" && continueButtonRef) {
                  continueButtonRef.setFocus();
                  console.log("✅ Focus restored to Continue button");
                } else if (entityActions) {
                  entityActions.setFocus();
                  console.log("✅ Focus restored to entity actions row");
                }
              }, 100);
            }}
            showBackground={false}
            title={' '}
            x={600}
            y={120}
          />
        </View>
      </Show>

      <View style={{ width: 1920, height: 1080 }}>
        <View
          style={{
            width: 1920,
            height: 1080,
            color: hexColor("#000000")
          }}
        />

        <Show when={backgroundImageEntity()}>
          <View
            // src={backgroundImageEntity()}
            src={fixImageUrl(backgroundImageEntity())}
            style={{
              width: 1920,
              height: 1080,
              objectFit: "cover",
              objectPosition: "center center",
            }}
            x={0}
            y={0}
          />
        </Show>

        {/* Jetstream-style scrims: left (for text) + bottom (for suggestions) */}
        <View src="assets/entity-scrim-left.png" width={1920} height={1080} x={0} y={0} />
        <View src="assets/entity-scrim-bottom.png" width={1920} height={1080} x={0} y={0} />
      </View>

      <View>
        <View
          onEscape={() => {
            // Handle PIN entry escape
            if (showPinEntry()) {
              console.log("🚫 Canceling PIN entry on Entity page");
              setShowPinEntry(false);
              setPinAction(null);
              setPinErrorMessage("");
              return;
            }

            if (!location.pathname.startsWith("/entity")) return;

            if (isVideoPlaying()) {
              videoPlayer.destroy().finally(() => setIsVideoPlaying(false));
            } else {
              document.getElementsByTagName("canvas")[0].focus();

              // ✅ Navigate back multiple steps if we came from suggestion
              if (returnToOriginalPage()) {
                console.log("🔙 Navigating back 2 steps (skipping suggestion origin)");
                setTimeout(() => {
                  navigate(-2);
                  setIsPlaying(true);
                }, 100);
              } else {
                console.log("🔙 Navigating back 1 step (normal)");
                setTimeout(() => {
                  navigate(-1);
                  setIsPlaying(true);
                }, 100);
              }
            }
          }}
          src="assets/hero-gradient-mask.png"
        >
          <View color={backgroundImageEntity() ? hexColor("#00000000") : hexColor("#00000000")}>
            <View>
              <View
                onLeft={() => true}
                x={70}
                y={suggestionsLifted() ? -640 : 0}
                transition={{ y: { duration: 350, easing: "ease-in-out" } }}
              >
                <Show
                  when={isReady()}
                  fallback={
                    <View x={650} y={400}>
                      <LoadingIndicator isCategoriesIndicator={false} />
                    </View>
                  }
                >
                  <ContentBlock
                    y={150}
                    x={params.type == "series" ? -54 : 46}
                    content={data().heroContent}
                  />
                </Show>

                <Row
                  ref={entityActions}
                  x={46}
                  y={600}
                  scroll="none"
                  height={90}
                  width={savedDataShow() ? 700 : 640}
                  gap={30}
                >
                  {params.type === "movie" && (
                    <>
                      <View
                        ref={playButtonRef}
                        style={{
                          color: "#C9C7CC",
                          focus: { color: "#ffffff", scale: 1.05 },
                          borderRadius: 8,
                          transition: { scale: { duration: 200, easing: "ease-in-out" } },
                        }}
                        width={210}
                        height={64}
                        autofocus
                        onEnter={onEnterTrailer}
                        onDown={() => {
                          if (columnRef) {
                            columnRef.setFocus();
                          }
                        }}
                      >
                        <View
                          skipFocus
                          src="assets/play-icon.png"
                          x={21}
                          y={20}
                          width={24}
                          height={24}
                        />
                        <Text
                          skipFocus
                          x={54}
                          y={20}
                          fontSize={26}
                          color={hexColor("#1A1A1A")}
                        >
                          Watch now
                        </Text>
                      </View>

                      <Show when={savedDataShow()}>
                        <View
                          ref={continueButtonRef}
                          onEnter={onVodResume}
                          onDown={() => {
                            if (columnRef) {
                              columnRef.setFocus();
                            }
                          }}
                          style={{
                            color: "#FFFFFF33",
                            focus: { color: "#ffffff", scale: 1.05 },
                            borderRadius: 8,
                            transition: { scale: { duration: 200, easing: "ease-in-out" } },
                          }}
                          width={315}
                          height={64}
                        >
                          <View direction="row" x={24} y={18} gap={12}>
                            <View
                              width={30}
                              height={30}
                              src="https://cdn-icons-png.flaticon.com/512/3318/3318600.png"
                            />
                            <Text
                              style={{
                                color: "#ffffff",
                                focus: { color: "#000000" }
                              }}
                              fontSize={26}
                              x={36}
                              y={2}
                            >
                              Continue Watching
                            </Text>
                          </View>
                        </View>
                      </Show>
                    </>
                  )}
                </Row>

                <View y={750} height={430} forwardFocus={1}>
                  <Text skipFocus fontSize={32} y={0} x={46} color={hexColor("#8E8E93")}>
                    Suggestions
                  </Text>

                  <Show
                    when={recommendations() && recommendations().length > 0}
                  >
                    <View x={41} y={82} forwardFocus={0}>
                      <Row
                        ref={columnRef}
                        gap={30}
                        scroll="edge"
                        selected={lastSuggestionIndex()}
                        width={1780}
                        onFocus={() => setSuggestionsLifted(true)}
                        onBlur={() => setSuggestionsLifted(false)}
                        onUp={() => {
                          if (entityActions) {
                            entityActions.setFocus();
                          }
                        }}
                      >
                        <For each={recommendations().slice(0, 20)}>
                          {(item: any, i) => (
                            <SuggestionItem {...item} index={i()} />
                          )}
                        </For>
                      </Row>
                    </View>
                  </Show>
                </View>

                <View ref={backdropRef} style={Backdrop} transition={{ alpha: true, y: true }} />
              </View>
              <View
                alpha={backdropAlpha()}
                color={hexColor("#000000")}
                zIndex={200}
                transition={{ alpha: true }}
              />
            </View>
          </View>
        </View>
      </View>
    </Show>
  );
};

export default Entity;